import React, { useEffect, useRef } from "react";
import { FormNotice } from "~/molecules/FormNotice";
import { colors } from "~/utils/colors";
import {
  ensureHubSpotDeveloperScript,
  ensureHubSpotFormElementInit,
  getHubSpotDeveloperScriptSrc,
  HUBSPOT_PORTAL_ID,
  HUBSPOT_REGION,
  resetHubSpotFormElement,
} from "~/utils/hubspotEmbed";
import { breakPoints, fontFamily } from "~/utils/variables";

export { HUBSPOT_PORTAL_ID, HUBSPOT_REGION };
export const HUBSPOT_DEVELOPER_SCRIPT = getHubSpotDeveloperScriptSrc();

const getScrollOffset = () => (window.matchMedia("(max-width: 767px)").matches ? 88 : 130);

const scrollToFormTop = (element: HTMLElement) => {
  const scrollTarget = element.closest<HTMLElement>("[data-hubspot-form-scroll-anchor]") ?? element;
  const top = scrollTarget.getBoundingClientRect().top + window.scrollY - getScrollOffset();
  window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
};

const TEXTAREA_ERROR_SELECTORS =
  ".hsfc-FieldFooter, .hsfc-ErrorAlert, [data-hsfc-id='FieldFooter'], [data-hsfc-id='ErrorAlert'], [role='alert']";

const isVisibleError = (element: Element): element is HTMLElement => {
  if (!(element instanceof HTMLElement)) return false;
  if (!element.textContent?.trim()) return false;

  const style = window.getComputedStyle(element);
  return style.display !== "none" && style.visibility !== "hidden";
};

const getVerticalGap = (topElement: HTMLElement, bottomElement: HTMLElement) =>
  bottomElement.getBoundingClientRect().top - topElement.getBoundingClientRect().bottom;

const findClosestFieldError = (fieldInput: HTMLElement, container: HTMLElement): HTMLElement | null => {
  let closest: HTMLElement | null = null;
  let closestDistance = Infinity;

  for (const candidate of container.querySelectorAll<HTMLElement>(TEXTAREA_ERROR_SELECTORS)) {
    if (!isVisibleError(candidate)) continue;
    if (!(fieldInput.compareDocumentPosition(candidate) & Node.DOCUMENT_POSITION_FOLLOWING)) continue;

    const distance = getVerticalGap(fieldInput, candidate);
    if (distance >= -4 && distance < closestDistance && distance < 160) {
      closestDistance = distance;
      closest = candidate;
    }
  }

  return closest;
};

const getTargetErrorGap = (container: HTMLElement): number => {
  const shortInput = container.querySelector<HTMLElement>(
    ".hsfc-PhoneInput input, input[type='tel'], .hsfc-TextInput:not(.hsfc-TextareaInput)"
  );
  if (!shortInput) return 4;

  const shortInputError = findClosestFieldError(shortInput, container);
  if (!shortInputError) return 4;

  const gap = getVerticalGap(shortInput, shortInputError);
  return gap > 0 && gap < 30 ? gap : 4;
};

const tightenTextareaFieldErrors = (container: HTMLElement) => {
  const targetGap = getTargetErrorGap(container);

  container.querySelectorAll("textarea, .hsfc-TextareaInput").forEach((textarea) => {
    if (!(textarea instanceof HTMLElement)) return;

    const error = findClosestFieldError(textarea, container);
    if (!error) return;

    error.style.setProperty("margin-top", "0px", "important");
    error.style.removeProperty("margin-bottom");
    void error.offsetHeight;

    const naturalGap = getVerticalGap(textarea, error);
    const marginTop = Math.round(2 - (naturalGap - targetGap));

    error.style.setProperty("margin-top", `${marginTop}px`, "important");
  });
};

type Props = {
  hubspotFormId?: string | null;
  defaultFormId?: string;
  className?: string;
  formNoticeClassName?: string;
  submitButtonGradient?: string;
  submitButtonBackgroundColor?: string;
  variant?: "default" | "contact";
  /** true のとき見出し・本文・規約など入力項目以外は HubSpot の見た目を優先する */
  preserveHubSpotContentStyles?: boolean;
};

export const HubSpotEmbedForm: React.FC<Props> = ({
  hubspotFormId,
  defaultFormId,
  className = "",
  formNoticeClassName = "",
  submitButtonGradient = colors.brandGradient,
  submitButtonBackgroundColor = colors.orange,
  variant = "default",
  preserveHubSpotContentStyles = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const formElementRef = useRef<HTMLDivElement>(null);
  const formId = hubspotFormId || defaultFormId;

  useEffect(() => {
    if (!formId) {
      return undefined;
    }

    let cancelled = false;

    ensureHubSpotDeveloperScript().then(() => {
      if (cancelled) {
        return;
      }

      const formElement = formElementRef.current;
      if (formElement) {
        ensureHubSpotFormElementInit(formElement);
      }
    });

    return () => {
      cancelled = true;
      if (formElementRef.current) {
        resetHubSpotFormElement(formElementRef.current);
      }
    };
  }, [formId]);

  useEffect(() => {
    if (!formId) return;

    let animationFrameId: number | null = null;

    const scheduleTightenTextareaErrors = () => {
      if (animationFrameId !== null) return;

      animationFrameId = window.requestAnimationFrame(() => {
        animationFrameId = null;
        if (containerRef.current) {
          tightenTextareaFieldErrors(containerRef.current);
        }
      });
    };

    const handleFormInteraction = (event: Event) => {
      const detail = (event as CustomEvent<{ formId?: string }>).detail;
      if (detail?.formId && detail.formId !== formId) return;

      window.setTimeout(scheduleTightenTextareaErrors, 0);
    };

    const handleNavigateStep = (event: Event) => {
      const detail = (event as CustomEvent<{ formId?: string }>).detail;
      if (detail?.formId && detail.formId !== formId) return;

      const container = containerRef.current;
      if (!container) return;

      window.setTimeout(() => {
        scrollToFormTop(container);
        scheduleTightenTextareaErrors();
      }, 50);
    };

    const observer = new MutationObserver(scheduleTightenTextareaErrors);
    if (containerRef.current) {
      observer.observe(containerRef.current, {
        childList: true,
        characterData: true,
        subtree: true,
      });
      scheduleTightenTextareaErrors();
    }

    window.addEventListener("hs-form-event:on-ready", handleFormInteraction);
    window.addEventListener("hs-form-event:on-submission:failed", handleFormInteraction);
    window.addEventListener("hs-form-event:on-interaction:navigate:next", handleNavigateStep);
    window.addEventListener("hs-form-event:on-interaction:navigate:previous", handleNavigateStep);

    return () => {
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }
      observer.disconnect();
      window.removeEventListener("hs-form-event:on-ready", handleFormInteraction);
      window.removeEventListener("hs-form-event:on-submission:failed", handleFormInteraction);
      window.removeEventListener("hs-form-event:on-interaction:navigate:next", handleNavigateStep);
      window.removeEventListener("hs-form-event:on-interaction:navigate:previous", handleNavigateStep);
    };
  }, [formId]);

  if (!hubspotFormId && !defaultFormId) {
    return null;
  }

  return (
    <>
      <style jsx>{`
        .hubspot-form {
          --hsf-global__font-family: ${fontFamily.insight};
          --hsf-global__font-size: 14px;
          --hsf-global__color: ${colors.defaultText};
          --hsf-global-error__color: ${colors.error};
          --hsf-background__background-color: transparent;
          --hsf-background__padding: 0;
          --hsf-background__border-width: 0;
          --hsf-row__vertical-spacing: 20px;
          --hsf-row__horizontal-spacing: 10px;
          --hsf-module__vertical-spacing: 3px;
          --hsf-field-label__font-family: ${fontFamily.insight};
          --hsf-field-label__font-size: 14px;
          --hsf-field-label__color: ${colors.defaultText};
          --hsf-field-label-requiredindicator__color: ${colors.error};
          --hsf-field-description__color: ${colors.lightText};
          --hsf-field-radio__padding: 8px;
          --hsf-field-radio__background-color: ${colors.white};
          --hsf-field-radio__color: ${colors.black};
          --hsf-field-radio__border-color: ${colors.gray4};
          --hsf-field-radio__border-width: 1px;
          --hsf-field-radio__border-style: solid;
          --hsf-field-checkbox__padding: 7px;
          --hsf-field-checkbox__background-color: ${colors.white};
          --hsf-field-checkbox__color: ${colors.black};
          --hsf-field-checkbox__border-color: ${colors.gray4};
          --hsf-field-checkbox__border-width: 1px;
          --hsf-field-checkbox__border-style: solid;
          --hsf-button__font-family: ${fontFamily.insight};
          --hsf-button__font-size: 14px;
          --hsf-button__color: ${colors.white};
          --hsf-button__background-color: ${submitButtonBackgroundColor};
          --hsf-button__background-image: ${submitButtonGradient};
          --hsf-button__border-radius: 50px;
          --hsf-button__padding: 12px 20px;
          --hsf-button__box-shadow: none;
          --hsf-richtext__font-family: ${fontFamily.insight};
          --hsf-richtext__font-size: 12px;
          --hsf-richtext__color: ${colors.lightText};
          --hsf-erroralert__font-size: 12px;
          --hsf-erroralert__color: ${colors.error};
          --hsf-default-background__padding: 0;
          --hsf-default-row__horizontal-spacing: 10px;
          --hsf-default-row__vertical-spacing: 20px;
          --hsf-default-module__vertical-spacing: 3px;
          --hsf-default-field-label__font-size: 14px;
          --hsf-default-button__padding: 12px 20px;
          min-height: 480px;
          width: 100%;
        }
        .hubspot-form :global(.hs-form-html) {
          min-height: 440px;
        }
        .hubspot-form :global(.hsfc-Form) {
          font-family: ${fontFamily.insight};
        }
        .hubspot-form :global(.hsfc-Step__Content) {
          padding: 0;
        }
        .hubspot-form :global(.hsfc-FieldLabel) {
          font-weight: 700;
          line-height: 1.5;
        }
        .hubspot-form :global(.hsfc-RadioFieldGroup__Options .hsfc-FieldLabel),
        .hubspot-form :global(.hsfc-CheckboxFieldGroup__Options .hsfc-FieldLabel),
        .hubspot-form :global(.hsfc-FieldLabel:has(.hsfc-RadioInput)),
        .hubspot-form :global(.hsfc-FieldLabel:has(.hsfc-CheckboxInput)) {
          font-weight: 400;
        }
        .hubspot-form :global(.hsfc-RadioFieldGroup__Options .hsfc-FieldLabel strong),
        .hubspot-form :global(.hsfc-CheckboxFieldGroup__Options .hsfc-FieldLabel strong),
        .hubspot-form :global(.hsfc-FieldLabel:has(.hsfc-RadioInput) strong),
        .hubspot-form :global(.hsfc-FieldLabel:has(.hsfc-CheckboxInput) strong) {
          font-weight: 400;
        }
        .hubspot-form :global(.hsfc-FieldDescription) {
          color: ${colors.lightText};
          font-size: 12px;
          line-height: 1.65;
        }
        .hubspot-form :global(.hsfc-FieldFooter) {
          color: ${colors.error};
          font-size: 12px;
          line-height: 1.45;
          margin: 2px 0 0;
        }
        .hubspot-form :global(.hsfc-ErrorAlert) {
          color: ${colors.error};
          font-size: 12px;
          line-height: 1.45;
          margin: 2px 0 0;
        }
        .hubspot-form :global([data-hsfc-id="Renderer"] .hsfc-Module + .hsfc-Module:has(.hsfc-ErrorAlert)),
        .hubspot-form :global([data-hsfc-id="Renderer"] .hsfc-Module + .hsfc-Module:has(.hsfc-FieldFooter:not(:empty))) {
          margin-top: -3px !important;
        }
        .hubspot-form :global(.hsfc-NavigationRow__Alerts:not(:empty)) {
          margin-bottom: 6px;
          margin-top: 0;
        }
        .hubspot-form :global(.hsfc-TextInput),
        .hubspot-form :global(.hsfc-TextareaInput),
        .hubspot-form :global(.hsfc-SelectInput),
        .hubspot-form :global(.hsfc-PhoneInput input),
        .hubspot-form :global(.hsfc-DateInput input) {
          background: ${colors.white};
          border: 1px solid ${colors.gray4};
          border-radius: 8px;
          box-sizing: border-box;
          color: ${colors.defaultText};
          font-family: ${fontFamily.insight};
          font-size: 14px;
          line-height: 1.5;
          max-width: 100%;
          padding: 11px 14px;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
          width: 100%;
        }
        .hubspot-form :global(.hsfc-TextInput::placeholder),
        .hubspot-form :global(.hsfc-TextareaInput::placeholder) {
          color: ${colors.littleBrightGray};
        }
        .hubspot-form :global(.hsfc-TextInput:focus),
        .hubspot-form :global(.hsfc-TextareaInput:focus),
        .hubspot-form :global(.hsfc-SelectInput:focus),
        .hubspot-form :global(.hsfc-PhoneInput input:focus),
        .hubspot-form :global(.hsfc-DateInput input:focus) {
          border-color: ${colors.orange};
          box-shadow: 0 0 0 3px ${colors.lightOrange};
          outline: none;
        }
        .hubspot-form :global(.hsfc-TextareaInput) {
          min-height: 180px;
        }
        .hubspot-form :global(.hsfc-FieldLabel:has(.hsfc-FieldLabel__RequiredIndicator) > span) {
          display: inline;
        }
        .hubspot-form :global(.hsfc-FieldLabel__RequiredIndicator) {
          align-items: center;
          color: transparent;
          display: inline-flex !important;
          font-size: 0;
          line-height: 0;
          margin: 0 0 0 6px;
          position: static;
          vertical-align: middle;
          white-space: nowrap;
        }
        .hubspot-form :global(.hsfc-FieldLabel__RequiredIndicator::before) {
          align-items: center;
          background: transparent;
          border: 1px solid ${colors.error};
          border-radius: 4px;
          box-sizing: border-box;
          color: ${colors.error};
          content: "必須";
          display: inline-flex;
          font-family: ${fontFamily.insight};
          font-size: 10px;
          font-weight: 700;
          height: 18px;
          justify-content: center;
          letter-spacing: 0.04em;
          line-height: 1;
          padding: 0 5px;
        }
        .hubspot-form :global(.hsfc-RichText),
        .hubspot-form :global(.hsfc-LegalConsentContainer) {
          color: ${colors.lightText};
          font-size: 12px;
          line-height: 1.8;
        }
        .hubspot-form :global(.hsfc-RichText p),
        .hubspot-form :global(.hsfc-LegalConsentContainer p) {
          margin: 0 0 0.75em;
        }
        .hubspot-form :global(.hsfc-RichText p:last-child),
        .hubspot-form :global(.hsfc-LegalConsentContainer p:last-child) {
          margin-bottom: 0;
        }
        .hubspot-form :global(.hsfc-RichText ul),
        .hubspot-form :global(.hsfc-LegalConsentContainer ul) {
          list-style: disc;
          margin: 0.5em 0 1em;
          padding-left: 1.4em;
        }
        .hubspot-form :global(.hsfc-RichText ol),
        .hubspot-form :global(.hsfc-LegalConsentContainer ol) {
          list-style: decimal;
          margin: 0.5em 0 1em;
          padding-left: 1.4em;
        }
        .hubspot-form :global(.hsfc-RichText li),
        .hubspot-form :global(.hsfc-LegalConsentContainer li) {
          line-height: 1.8;
          margin: 0.35em 0;
          padding-left: 0.15em;
        }
        .hubspot-form :global(.hsfc-RichText li::marker),
        .hubspot-form :global(.hsfc-LegalConsentContainer li::marker) {
          color: ${colors.lightText};
        }
        .hubspot-form :global(.hsfc-RichText a),
        .hubspot-form :global(.hsfc-LegalConsentContainer a) {
          color: ${colors.orange};
          text-decoration: underline;
        }
        .hubspot-form :global(.hsfc-RichText a:hover),
        .hubspot-form :global(.hsfc-LegalConsentContainer a:hover) {
          text-decoration: none;
        }
        .hubspot-form :global(.hsfc-NavigationRow) {
          margin-top: 4px;
        }
        .hubspot-form :global(.hsfc-NavigationRow__Buttons) {
          gap: 10px;
        }
        .hubspot-form :global(.hsfc-Button) {
          border: none;
          border-radius: 50px;
          box-sizing: border-box;
          cursor: pointer;
          display: block;
          font-weight: 700;
          letter-spacing: 0.04em;
          line-height: 1.4;
          margin: 0;
          max-width: 100%;
          transition: background-color 0.2s ease, border-color 0.2s ease, opacity 0.2s ease;
          width: 100%;
        }
        .hubspot-form :global(.hsfc-NavigationRow__Buttons .hsfc-Button[type="button"]) {
          background-color: ${colors.white};
          background-image: none;
          border: 1px solid ${colors.orange};
          color: ${colors.orange};
        }
        .hubspot-form :global(.hsfc-NavigationRow__Buttons .hsfc-Button[type="button"]:not([disabled]):hover),
        .hubspot-form :global(.hsfc-NavigationRow__Buttons .hsfc-Button[type="button"]:not([disabled]):focus),
        .hubspot-form :global(.hsfc-NavigationRow__Buttons .hsfc-Button[type="button"]:not([disabled]):active) {
          background-color: ${colors.lightOrange};
          background-image: none;
          border-color: ${colors.orange};
          color: ${colors.orange};
          opacity: 1;
          transform: none;
        }
        .hubspot-form :global(.hsfc-NavigationRow__Buttons .hsfc-Button[type="submit"]) {
          background-color: ${submitButtonBackgroundColor};
          background-image: ${submitButtonGradient};
          border: none;
          color: ${colors.white};
        }
        .hubspot-form :global(.hsfc-NavigationRow__Buttons .hsfc-Button[type="submit"]:not([disabled]):hover),
        .hubspot-form :global(.hsfc-NavigationRow__Buttons .hsfc-Button[type="submit"]:not([disabled]):focus),
        .hubspot-form :global(.hsfc-NavigationRow__Buttons .hsfc-Button[type="submit"]:not([disabled]):active) {
          background-color: ${submitButtonBackgroundColor};
          background-image: ${submitButtonGradient};
          opacity: 0.9;
          transform: none;
        }
        .hubspot-form :global(.hsfc-NavigationRow__Buttons .hsfc-Button[type="submit"]:active) {
          opacity: 0.85;
        }
        .hubspot-form :global(.hsfc-Heading),
        .hubspot-form :global(.hsfc-FormTitle) {
          display: none;
        }
        /* 入力項目以外は HubSpot 側のスタイルを優先（見出し・本文・規約など） */
        .hubspot-form.hubspot-form--hubspot-content {
          --hsf-richtext__font-family: unset;
          --hsf-richtext__font-size: unset;
          --hsf-richtext__color: unset;
        }
        .hubspot-form.hubspot-form--hubspot-content :global(.hsfc-Heading),
        .hubspot-form.hubspot-form--hubspot-content :global(.hsfc-FormTitle) {
          display: revert !important;
        }
        .hubspot-form.hubspot-form--hubspot-content :global(.hsfc-RichText),
        .hubspot-form.hubspot-form--hubspot-content :global(.hsfc-LegalConsentContainer),
        .hubspot-form.hubspot-form--hubspot-content :global(.hsfc-RichText p),
        .hubspot-form.hubspot-form--hubspot-content :global(.hsfc-LegalConsentContainer p),
        .hubspot-form.hubspot-form--hubspot-content :global(.hsfc-RichText li),
        .hubspot-form.hubspot-form--hubspot-content :global(.hsfc-LegalConsentContainer li),
        .hubspot-form.hubspot-form--hubspot-content :global(.hsfc-RichText a),
        .hubspot-form.hubspot-form--hubspot-content :global(.hsfc-LegalConsentContainer a),
        .hubspot-form.hubspot-form--hubspot-content :global(.hsfc-RichText li::marker),
        .hubspot-form.hubspot-form--hubspot-content :global(.hsfc-LegalConsentContainer li::marker) {
          color: unset;
          font-size: unset;
          line-height: unset;
          margin: unset;
          padding: unset;
          text-decoration: unset;
        }
        /* サイト全体の list-style:none だけ打ち消し、見た目は HubSpot に任せる */
        .hubspot-form.hubspot-form--hubspot-content :global(.hsfc-RichText ul),
        .hubspot-form.hubspot-form--hubspot-content :global(.hsfc-LegalConsentContainer ul) {
          list-style: disc;
          margin: unset;
          padding-left: 1.5em;
        }
        .hubspot-form.hubspot-form--hubspot-content :global(.hsfc-RichText ol),
        .hubspot-form.hubspot-form--hubspot-content :global(.hsfc-LegalConsentContainer ol) {
          list-style: decimal;
          margin: unset;
          padding-left: 1.5em;
        }
        .formNotice {
          border-top: 1px solid ${colors.gray3};
          margin-top: 20px;
          padding-top: 20px;
        }
        @media (${breakPoints.sp}) {
          .hubspot-form {
            --hsf-row__vertical-spacing: 18px;
            --hsf-row__horizontal-spacing: 8px;
            --hsf-default-row__vertical-spacing: 18px;
            --hsf-default-row__horizontal-spacing: 8px;
            min-height: 420px;
          }
          .hubspot-form :global([data-hsfc-id="Renderer"] .hsfc-Row:not([style*="display:none"]):not([style*="display: none"])) {
            display: grid !important;
            flex-direction: unset !important;
            gap: var(--hsf-row__horizontal-spacing, 8px) !important;
            grid-template-columns: repeat(auto-fit, minmax(0, 1fr)) !important;
          }
          .hubspot-form :global([data-hsfc-id="Renderer"] .hsfc-Row[style*="display:none"]),
          .hubspot-form :global([data-hsfc-id="Renderer"] .hsfc-Row[style*="display: none"]) {
            display: none !important;
            height: 0 !important;
            margin: 0 !important;
            overflow: hidden !important;
            padding: 0 !important;
          }
          .hubspot-form :global([data-hsfc-id="Renderer"] .hsfc-Row:not([style*="display:none"]):not([style*="display: none"]) > *) {
            min-width: 0;
          }
          .hubspot-form :global([data-hsfc-id="Renderer"] .hsfc-NavigationRow) {
            margin-top: 8px !important;
          }
          .hubspot-form :global([data-hsfc-id="Renderer"] .hsfc-NavigationRow__Alerts:empty) {
            display: none;
            margin: 0 !important;
          }
          .hubspot-form :global([data-hsfc-id="Renderer"] .hsfc-NavigationRow__Alerts:not(:empty)) {
            margin-bottom: 4px !important;
            margin-top: 0 !important;
          }
          .hubspot-form :global([data-hsfc-id="Renderer"] .hsfc-NavigationRow__Buttons) {
            flex-direction: row !important;
            gap: 10px !important;
            justify-content: center !important;
            width: 100%;
          }
          .hubspot-form :global([data-hsfc-id="Renderer"] .hsfc-NavigationRow__Buttons > *) {
            flex: 1 1 0 !important;
            min-width: 0 !important;
            width: auto !important;
          }
          .hubspot-form :global([data-hsfc-id="Renderer"] .hsfc-NavigationRow__Buttons .hsfc-Button[type="button"]) {
            box-sizing: border-box;
            display: inline-flex;
            font-size: 14px;
            justify-content: center;
            padding: 11px 14px;
            width: 100% !important;
          }
          .hubspot-form :global([data-hsfc-id="Renderer"] .hsfc-NavigationRow__Buttons .hsfc-Button[type="submit"]) {
            box-sizing: border-box;
            display: inline-flex;
            font-size: 14px;
            justify-content: center;
            padding: 11px 14px;
            width: 100% !important;
          }
          .hubspot-form :global(.hsfc-TextInput),
          .hubspot-form :global(.hsfc-TextareaInput),
          .hubspot-form :global(.hsfc-SelectInput),
          .hubspot-form :global(.hsfc-PhoneInput input),
          .hubspot-form :global(.hsfc-DateInput input) {
            font-size: 16px;
          }
          .formNotice {
            margin-top: 16px;
            padding-top: 16px;
          }
        }
        .hubspot-form--contact {
          --hsf-global__font-size: 15px;
          --hsf-field-label__font-size: 15px;
          --hsf-row__vertical-spacing: 24px;
          --hsf-module__vertical-spacing: 5px;
          --hsf-button__font-size: 15px;
          --hsf-button__padding: 14px 22px;
          --hsf-richtext__font-size: 13px;
          --hsf-erroralert__font-size: 13px;
          --hsf-default-row__vertical-spacing: 24px;
          --hsf-default-module__vertical-spacing: 5px;
          --hsf-default-field-label__font-size: 15px;
          --hsf-default-button__padding: 14px 22px;
        }
        .hubspot-form--contact :global(.hsfc-FieldLabel) {
          line-height: 1.55;
        }
        .hubspot-form--contact :global(.hsfc-FieldDescription) {
          font-size: 13px;
          line-height: 1.7;
        }
        .hubspot-form--contact :global(.hsfc-FieldFooter),
        .hubspot-form--contact :global(.hsfc-ErrorAlert) {
          font-size: 13px;
          line-height: 1.5;
        }
        .hubspot-form--contact :global(.hsfc-TextInput),
        .hubspot-form--contact :global(.hsfc-TextareaInput),
        .hubspot-form--contact :global(.hsfc-SelectInput),
        .hubspot-form--contact :global(.hsfc-PhoneInput input),
        .hubspot-form--contact :global(.hsfc-DateInput input) {
          font-size: 15px;
          line-height: 1.55;
          padding: 12px 16px;
        }
        .hubspot-form--contact :global(.hsfc-TextareaInput) {
          min-height: 200px;
        }
        .hubspot-form--contact :global(.hsfc-RichText),
        .hubspot-form--contact :global(.hsfc-LegalConsentContainer) {
          font-size: 13px;
          line-height: 1.8;
        }
        .hubspot-form--contact :global(.hsfc-NavigationRow) {
          margin-top: 8px;
        }
        .hubspot-form--contact :global(.hsfc-NavigationRow__Buttons) {
          gap: 12px;
        }
        .hubspot-form--contact :global(.hsfc-FieldLabel__RequiredIndicator::before) {
          font-size: 11px;
          height: 19px;
          padding: 0 6px;
        }
        .hubspot-form--contact .formNotice {
          margin-top: 24px;
          padding-top: 24px;
        }
        @media (${breakPoints.sp}) {
          .hubspot-form--contact {
            --hsf-row__vertical-spacing: 22px;
            --hsf-default-row__vertical-spacing: 22px;
          }
          .hubspot-form--contact :global([data-hsfc-id="Renderer"] .hsfc-NavigationRow__Buttons .hsfc-Button[type="button"]),
          .hubspot-form--contact :global([data-hsfc-id="Renderer"] .hsfc-NavigationRow__Buttons .hsfc-Button[type="submit"]) {
            font-size: 15px;
            padding: 12px 16px;
          }
          .hubspot-form--contact .formNotice {
            margin-top: 20px;
            padding-top: 20px;
          }
        }
      `}</style>
      <div
        ref={containerRef}
        className={`hubspot-form ${variant === "contact" ? "hubspot-form--contact" : ""} ${
          preserveHubSpotContentStyles ? "hubspot-form--hubspot-content" : ""
        } ${className}`.trim()}
      >
        <div
          key={formId}
          ref={formElementRef}
          className="hs-form-html"
          data-region={HUBSPOT_REGION}
          data-form-id={formId}
          data-portal-id={HUBSPOT_PORTAL_ID}
        />
        <FormNotice className={`formNotice ${formNoticeClassName}`.trim()} />
      </div>
    </>
  );
};
